import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// The app's main anchor: both AI1-creation flows presented as co-equal primary
// tasks. Dashboard stays a quiet admin link in the top bar — it is not a
// creation flow and does not belong here.
const FLOWS = [
  {
    to: "/objections",
    label: "Objection AI1",
    blurb:
      "Turn DOB objection sources (PDF, Excel, CSV, BAX) into a properly formatted AI1 objection response.",
    cta: "Upload objections",
    accent: "primary" as const,
  },
  {
    to: "/paa",
    label: "PAA AI1",
    blurb:
      "Turn an updated plan set into a Post-Approval Amendment AI1 — revised sheets, Section 2, and the change summary.",
    cta: "Upload the plan PDF",
    accent: "sage" as const,
  },
];

export function ChooseFlowPage() {
  return (
    <div className="animate-reveal mx-auto max-w-4xl">
      <div className="mb-10 text-center">
        <div className="label mb-2">AI1 Builder</div>
        <h1 className="text-[clamp(2.25rem,4.5vw,3.25rem)] font-medium tracking-tight text-ink">
          What are you filing today?
        </h1>
        <p className="mx-auto mt-3 max-w-xl text-[1rem] leading-relaxed text-ink-soft">
          Pick a flow to begin. Both produce a ready-to-file AI1 PDF.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        {FLOWS.map((f) => (
          <FlowCard key={f.to} {...f} />
        ))}
      </div>
    </div>
  );
}

function FlowCard({
  to,
  label,
  blurb,
  cta,
  accent,
}: {
  to: string;
  label: string;
  blurb: string;
  cta: string;
  accent: "primary" | "sage";
}) {
  return (
    <Link
      to={to}
      className={cn(
        "group relative flex flex-col rounded-lg border border-rule bg-surface p-7 shadow-soft transition-all",
        "hover:-translate-y-0.5 hover:shadow-lift focus-visible:outline-none focus-visible:shadow-ring",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "mb-5 inline-flex h-11 w-11 items-center justify-center rounded-full",
          accent === "primary" ? "bg-primary-soft text-primary-ink" : "bg-sage-soft text-sage-ink",
        )}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <path
            d="M12 16V4m0 0L7 9m5-5l5 5M5 20h14"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
      <h2 className="text-[1.4rem] font-medium tracking-tight text-ink">{label}</h2>
      <p className="mt-2 flex-1 text-[0.95rem] leading-relaxed text-ink-soft">{blurb}</p>
      <span
        className={cn(
          "mt-6 inline-flex items-center gap-1.5 text-[0.92rem] font-medium",
          accent === "primary" ? "text-primary" : "text-sage-ink",
        )}
      >
        {cta}
        <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>
          →
        </span>
      </span>
    </Link>
  );
}
